import React from "react";
import { Link } from "react-router-dom";

import freezerTruckImg from "../../assets/images/Home/freezer-truck-service-chiller-van.webp";

export const freezerTruckForRentSections = [
  {
    type: "ftfr-hero",
    title:
      "Freezer Truck for Rent in Dubai & Sharjah — Sub-Zero Reefer Trucks for Bulk Frozen Cargo Transport Across the UAE | Hire Today",
    paragraphs: [
      "Chiller Fleet Transport provides freezer trucks for rent across Dubai, maintaining sub-zero temperatures between -18°C and -25°C across payload capacities up to 15 tons. Businesses moving bulk frozen meat, seafood, and frozen food manufacturing output across UAE routes depend on heavy reefer truck capacity where sub-zero compliance across the full cargo body — not just at the sensor point — determines whether frozen cargo arrives in acceptable condition.",
      "Our fleet of 31 reefer trucks for rent operates across Dubai and Sharjah branches, available for both scheduled and same-day deployment. Every vehicle is assigned based on your tonnage requirement, temperature depth, and route — with sub-zero compliance standards confirmed before departure on every booking.",
      "Contact our team to arrange freezer truck rental in Dubai or Sharjah based on your frozen cargo tonnage and sub-zero temperature requirement.",
    ],
  },

  {
    type: "ftfr-businesses",
    title: "Businesses That Operate Our Freezer Truck Fleet Across Dubai & Sharjah",
    businesses: [
      {
        title: "Frozen Meat & Seafood Processors",
        description:
          "Processing facilities moving bulk frozen meat and seafood output to cold storage across Dubai operate on daily dispatch cycles where single-trip tonnage consistently exceeds van capacity and -25°C maintenance across the full load is non-negotiable. These operations require a freezer truck fleet that confirms sub-zero compliance before loading — not after departure.",
      },
      {
        title: "FMCG Frozen Food Distributors",
        description:
          "Regional distributors moving packaged frozen goods across supermarket chains run on fixed supply timelines where a single freezer truck covers multiple outlet restocking in one run. Delivery window compliance and sub-zero consistency across the full load across Dubai and Sharjah distribution networks define the operational standard for this client profile.",
      },
      {
        title: "Pharmaceutical Cold Chain Operators",
        description:
          <>Pharmaceutical companies moving temperature-sensitive frozen products between UAE import facilities, cold storage warehouses, and hospital distribution networks require documented sub-zero transport where temperature records and compliance confirmation accompany every delivery — not just the vehicle. Operations requiring both chilled and frozen zones in a single trip are better served by our <Link to="/refrigerated-trucks-for-rent" className="internal-link">refrigerated trucks for rent</Link>.</>,
      },
      {
        title: "Import & Export Cold Chain Operators",
        description:
          "Port-to-cold-storage and airport-to-warehouse frozen cargo movement across Dubai operates under timing pressure where sub-zero continuity between facility handover points determines import compliance outcomes. These operators require pre-staged reefer trucks ready before cargo clears the facility — not dispatched after.",
      },
    ],
  },

  {
    type: "ftfr-standards",
    title: "The Temperature Control Standards Used in Every Freezer Truck",
    description:
      "Each of our freezer trucks for rent in Dubai is cleared against five operational standards before the vehicle leaves our branch — because sub-zero frozen cargo compliance is confirmed through process, not assumed through equipment:",
    standards: [
      {
        label: "1. Temperature Depth Certification",
        description:
          "Our fleet maintains confirmed temperatures between -18°C and -25°C across the full cargo body — not just at the floor sensor point. Dual-sensor monitoring at both front and rear cargo zones confirms full-body temperature stability.",
      },
      {
        label: "2. Pre-Dispatch Seal Integrity",
        description:
          "Door seals are physically inspected before every deployment across Dubai branches. Seal degradation under repeated -25°C to 45°C temperature cycling is the most common undetected cause of sub-zero failure on extended UAE routes.",
      },
      {
        label: "3. Defrost Cycle Scheduling",
        description:
          "Automatic defrost cycles on inter-emirate freezer truck routes are checked and adjusted before departure. An unmanaged defrost cycle on a long-haul frozen cargo run allows internal temperature to rise above acceptable range for several minutes mid-route.",
      },
      {
        label: "4. Compressor Load Confirmation",
        description:
          "Compressor performance is verified against actual load weight and ambient temperature conditions before departure. A fully loaded 15 ton cargo body under UAE summer heat creates compressor demand that an unverified unit may not sustain across the full route duration.",
      },
      {
        label: "5. Load Distribution Protocol",
        description:
          "Cargo is staged and loaded in a sequence that maintains evaporator airflow across the full body interior. Incorrect load stacking blocks airflow and creates warm pockets in upper cargo layers that floor-level sensors cannot detect until delivery.",
      },
    ],
  },

  {
    type: "ftfr-failures",
    title: "Why Freezer Trucks Fail at Sub-Zero Scale in UAE Conditions — And How We Prevent It",
    description:
      "Most freezer truck failures in UAE conditions are operational — not mechanical. Here is where they happen and what we do about it:",
    failures: [
      {
        title: "Cargo Body Temperature Stratification",
        description:
          "Cold air from the refrigeration unit sinks while warm air rises — creating temperature layers across a large cargo body where upper layers may not reach -18°C even when the floor sensor reads correctly.",
        prevention:
          "Dual-sensor front and rear zone monitoring confirms full-body temperature before loading begins.",
      },
      {
        title: "Compressor Overload at Maximum Payload",
        description:
          "A fully loaded 15 ton frozen cargo body under 45°C UAE ambient heat places extreme demand on the refrigeration compressor — most operators load before confirming the unit can sustain that demand across the full route.",
        prevention:
          "Compressor load is verified against actual tonnage and temperature forecast before every departure.",
      },
      {
        title: "Defrost Cycle Timing on Extended Routes",
        description:
          "Refrigeration units run automatic defrost cycles that temporarily suspend cooling — on short urban routes this rarely matters, but on inter-emirate frozen cargo runs an incorrectly timed cycle allows internal temperature to rise above -18°C for several minutes mid-route.",
        prevention:
          "Defrost cycle scheduling is adjusted before dispatch on all inter-emirate bookings.",
      },
      {
        title: "Tail Lift Heat Exposure During Bulk Unloading",
        description:
          "Every tail lift operation opens the rear cargo door fully — exposing the entire back of a heavy cargo body to ambient UAE heat simultaneously. At truck scale this means a significantly greater cold air volume escapes per opening than on a van delivery.",
        prevention:
          "Unloading sequences at each delivery point are planned to minimize full door exposure time per stop.",
      },
      {
        title: "Door Seal Degradation Under Repeated Sub-Zero Cycling",
        description:
          "Door seals degrade faster under repeated -25°C to 45°C temperature cycling in UAE conditions than in any other operating environment. A degraded seal allows continuous ambient air infiltration the refrigeration unit must constantly compensate for — increasing compressor load while reducing temperature stability.",
        prevention:
          "Door seal condition is inspected as part of pre-dispatch vehicle checks at both Dubai and Sharjah branches.",
      },
    ],
  },

  {
    type: "ftfr-fleet-spec",
    title: "Freezer Truck Fleet — Sub-Zero Payload Capacity & Refrigeration Configuration",
    description:
      "Our freezer truck fleet across Dubai is configured specifically for sub-zero heavy cargo operations — covering payload capacities from 5 tons to 15 tons under confirmed -18°C to -25°C conditions. Every specification below reflects active fleet standards applied across every booking:",
    image: freezerTruckImg,
    imageAlt:
      "Freezer truck parked for bulk sub-zero frozen cargo transport across Dubai and Sharjah",
    specRows: [
      { spec: "Temperature Range", detail: "-18°C to -25°C" },
      { spec: "Payload Tiers", detail: "5, 7, 10 & 15 ton" },
      { spec: "Refrigeration Units", detail: "Heavy-duty diesel-powered reefer units" },
      { spec: "Dual-Zone Monitoring", detail: "Front and rear cargo zone sensors" },
      { spec: "Pre-Cooling", detail: "Mandatory full-body confirmation before loading" },
      { spec: "Defrost Management", detail: "Pre-dispatch cycle scheduling on inter-emirate routes" },
      { spec: "Tail Lift", detail: "Available on 7, 10 & 15 ton units" },
      { spec: "Door Configuration", detail: "Rear roller door" },
      { spec: "GPS", detail: "Fitted as standard across Dubai & Sharjah fleet" },
      { spec: "Fleet Brands", detail: "Isuzu, Hino, MAN, Mercedes" },
      { spec: "RTA Compliance", detail: "Full fleet verified" },
    ],
  },

  {
    type: "ftfr-pricing",
    title: "Freezer Truck Rental Rates — Pricing by Tonnage & Sub-Zero Temperature Requirement",
    descriptionMain:
      "Freezer truck rental rates across Dubai and Sharjah are structured around two factors simultaneously — tonnage tier and temperature depth requirement. Operating at -25°C carries a higher rate than -18°C due to increased refrigeration fuel consumption and extended pre-cooling windows required at deeper freeze. All rates include GPS tracking, dual-zone temperature monitoring, and pre-dispatch compliance checks as standard. UAE VAT applies where applicable.",
    rows: [
      {
        tonnage: "5 Ton",
        depth: "-18°C",
        dailyWithout: "AED 900 — AED 1,100",
        dailyWith: "AED 1,050 — AED 1,300",
        weekly: "AED 5,500 — AED 7,000",
        monthly: "AED 14,000 — AED 18,000",
      },
      {
        tonnage: "7 Ton",
        depth: "-18°C",
        dailyWithout: "AED 1,100 — AED 1,400",
        dailyWith: "AED 1,300 — AED 1,600",
        weekly: "AED 7,000 — AED 9,000",
        monthly: "AED 18,000 — AED 22,000",
      },
      {
        tonnage: "10 Ton",
        depth: "-18°C to -25°C",
        dailyWithout: "AED 1,400 — AED 1,700",
        dailyWith: "AED 1,600 — AED 1,950",
        weekly: "AED 9,000 — AED 11,500",
        monthly: "AED 22,000 — AED 27,000",
      },
      {
        tonnage: "15 Ton",
        depth: "-18°C to -25°C",
        dailyWithout: "AED 1,700 — AED 2,100",
        dailyWith: "AED 1,950 — AED 2,400",
        weekly: "AED 11,500 — AED 14,000",
        monthly: "AED 27,000 — AED 33,000",
      },
    ],
    descriptionNote:
      "Inter-emirate routes to Ras Al Khaimah and Fujairah carry a daily surcharge of AED 100 — AED 200 depending on tonnage tier. All prices are starting rates — final confirmation is provided based on your tonnage requirement, temperature depth, delivery route, and rental duration. Contact our team for an exact quote.",
  },

  {
    type: "ftfr-areas",
    title: "Areas We Serve Across Dubai, Sharjah And Fujairah",
    areas: [
      {
        title: "Jebel Ali",
        description:
          "Port-cleared frozen cargo at Jebel Ali operates under immediate onward movement pressure where waiting time directly increases sub-zero risk across bulk loads. Freezer trucks deployed to Jebel Ali routes are pre-cooled and staged before cargo release — removing the vehicle preparation gap that causes temperature exposure between port clearance and cold storage handover.",
      },
      {
        title: "Dubai Industrial Park",
        description:
          "Frozen food manufacturing facilities in Dubai Industrial Park generate bulk output that moves to cold storage in single heavy trips. Our reefer truck deployment here is structured around production schedules and cold storage receiving windows rather than standard delivery timing.",
      },
      {
        title: "Ras Al Khor",
        description:
          "Our primary Dubai dispatch base for freezer truck operations covers warehouse cold storage corridor movement across Ras Al Khor's concentrated logistics zone — where frozen cargo staging, pre-cooling confirmation, and same-day deployment are all managed from a single branch location.",
      },
      {
        title: "Sharjah Industrial Areas",
        description:
          "Cross-emirate frozen cargo originating from Sharjah Industrial Areas 1, 2, and 3 moves through one of the UAE's most active frozen processing-to-distribution corridors. Our Sharjah branch dispatches directly into this corridor without the cross-emirate coordination delay that Dubai-only operators cannot avoid.",
      },
      {
        title: "Hamriyah Free Zone",
        description:
          "Frozen import and export cargo moving through Hamriyah Free Zone operates under compliance documentation requirements where sub-zero temperature records accompany every consignment. Freezer truck deployment here is coordinated around free zone cargo release schedules and cold chain documentation timelines.",
      },
      {
        title: "DXB Airport Cargo Zone",
        description:
          "Frozen air freight cleared through DXB requires immediate sub-zero ground continuation where vehicle staging before cargo release determines whether temperature continuity holds through the airport-to-cold-storage handover. Our freezer trucks are pre-positioned before cargo clears the facility.",
      },
      {
        title: "Fujairah Port",
        description:
          "Frozen seafood landed at Fujairah Port moves onward to cold storage across the longest sustained transit of any inter-emirate route in the UAE. Compressor load is verified against tonnage and forecast ambient temperature before departure, since the crossing offers none of the recovery windows a short urban run allows.",
      },
      {
        title: "Fujairah Free Zone",
        description:
          "Imported frozen goods clearing through the free zone continue to cold storage and regional distribution under sub-zero continuity that must hold across the full corridor, not just at the handover points. Defrost cycle timing is set against actual transit duration before departure rather than left on default scheduling.",
      },
      {
        title: "Dibba & East Coast Distribution",
        description:
          "Frozen distribution running north from Fujairah City extends transit beyond the main corridor across coastal routes. These runs are sequenced single-direction with the highest-volume drop placed first, and scheduled on confirmed lead time from our Ras Al Khor and Sharjah branches rather than same-day standby dispatch.",
      },
    ],
  },

  {
    type: "ftfr-faq",
    title: "FAQs",
    faqs: [
      {
        q: "What is the deepest temperature your freezer trucks maintain?",
        a: "Our freezer truck fleet maintains temperatures down to -25°C across payload capacities from 5 tons to 15 tons. This depth covers the most demanding sub-zero frozen cargo requirements across Dubai routes including pharmaceutical, seafood, and frozen food manufacturing operations.",
      },
      {
        q: "How do you prevent temperature stratification across a large frozen cargo body?",
        a: "Through dual-sensor monitoring at both front and rear cargo zones — not just floor-level single-point sensors. Full-body temperature is confirmed stable before loading begins on every booking across our Dubai and Sharjah fleet.",
      },
      {
        q: "Can defrost cycles be managed on inter-emirate freezer truck routes?",
        a: "Yes. Defrost cycle scheduling is checked and adjusted before departure on all inter-emirate bookings — preventing mid-route temperature rise during automatic defrost cycles on long-haul frozen cargo runs between Dubai, Sharjah, and wider UAE destinations.",
      },
      {
        q: "What payload capacity do your freezer trucks reach?",
        a: "Up to 15 tons — covering bulk frozen cargo operations at processing facility, warehouse distribution, and large-scale FMCG supply chain scale across Dubai.",
      },
      {
        q: "Do your freezer trucks carry tail lifts for facilities without loading docks?",
        a: "Tail lifts are available on all 7, 10, and 15 ton freezer trucks across our Dubai fleet — covering loading and unloading operations at facilities without dedicated dock infrastructure.",
      },
      {
        q: "What is the difference between a freezer truck and a freezer van?",
        a: <>Payload scale and route duration. <Link to="/freezer-van-for-rent" className="internal-link">Freezer vans</Link> cover 1 to 1.5 ton urban frozen cargo delivery across short to medium routes. Freezer trucks cover 5 to 15 ton bulk frozen cargo movement across warehouse, industrial, and inter-emirate routes where van capacity is insufficient.</>,
      },
      {
        q: "What is the minimum rental period for a freezer truck?",
        a: "Daily rental is the minimum period available across all tonnage tiers. Weekly and monthly arrangements are offered at progressively lower per-day rates for operations requiring ongoing freezer truck availability across Dubai or Sharjah.",
      },
    ],
  },

  {
    type: "cta",
    title: "Arrange Freezer Truck Rental for Your Next Sub-Zero Bulk Delivery",
    paragraphs: [
      <>Our freezer truck fleet across Dubai and Sharjah is ready for your next sub-zero bulk booking. Call or WhatsApp <Link to="/contact-us" className="internal-link">our team</Link> directly with your tonnage requirement and temperature depth — deployment confirmation follows from there.</>,
    ],
  },
];
